import { useState } from 'react';
import type { Representative } from '../../types/representative';
import {
  Form,
  FormField,
  FormLabel,
  FormInput,
  FormPhone,
  FormMail,
  FormTextarea,
  FormCheckbox,
  Button,
} from '../ui';
import { formatPhoneNumber } from '../ui/FormPhone';

interface RepresentativeFormProps {
  representative: Representative | null;
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  isLoading: boolean;
  onClose: () => void;
}

function RepresentativeForm({
  representative,
  onSubmit,
  isLoading,
  onClose,
}: RepresentativeFormProps) {
  const [formData, setFormData] = useState({
    name: representative?.name || '',
    email: representative?.email || '',
    phone: representative?.phone ? formatPhoneNumber(representative.phone) : '',
    integration_key: representative?.integration_key?.toString() || '',
    notes: representative?.notes || '',
    active: representative?.active !== false,
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      setFormData((prev) => ({ ...prev, [name]: (e.target as HTMLInputElement).checked }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  return (
    <Form onSubmit={onSubmit}>
      <FormField>
        <FormLabel htmlFor="name" required>
          Nome
        </FormLabel>
        <FormInput
          id="name"
          name="name"
          type="text"
          maxLength={150}
          value={formData.name}
          onChange={handleChange}
          placeholder="Digite o nome do vendedor"
          required
        />
      </FormField>

      <FormField>
        <FormLabel htmlFor="email">
          E-mail
        </FormLabel>
        <FormMail
          id="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          placeholder="exemplo@email.com"
        />
      </FormField>

      <FormField>
        <FormLabel htmlFor="phone">
          Telefone
        </FormLabel>
        <FormPhone
          id="phone"
          name="phone"
          value={formData.phone}
          onChange={handleChange}
          placeholder="(00) 00000-0000"
        />
      </FormField>

      <FormField>
        <FormLabel htmlFor="integration_key">
          Chave de Integração
        </FormLabel>
        <FormInput
          id="integration_key"
          name="integration_key"
          type="number"
          value={formData.integration_key}
          onChange={handleChange}
          placeholder="Chave de referência para sistemas externos"
        />
      </FormField>

      <FormField>
        <FormLabel htmlFor="notes">
          Observações
        </FormLabel>
        <FormTextarea
          id="notes"
          name="notes"
          rows={4}
          value={formData.notes}
          onChange={handleChange}
          placeholder="Digite observações sobre o vendedor"
        />
      </FormField>

      <FormField>
        <div className="flex items-center">
          <FormCheckbox
            id="active"
            name="active"
            checked={formData.active}
            onChange={handleChange}
          />
          <FormLabel htmlFor="active" className="ml-2 mb-0">
            Ativo
          </FormLabel>
        </div>
      </FormField>

      <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
        <Button type="submit" isLoading={isLoading} className="sm:col-start-2">
          {representative ? 'Salvar' : 'Criar'}
        </Button>
        <Button type="button" variant="secondary" onClick={onClose} className="sm:col-start-1">
          Cancelar
        </Button>
      </div>
    </Form>
  );
}

export default RepresentativeForm;
